/**
 * 电商套图 · 管理后台统计 Tab
 */
import { useEffect, useState } from 'react';
import { ShoppingBag, TrendingUp, Layers, Loader2 } from 'lucide-react';
import { platformFetch } from '../../../platform/http';

interface SuiteStats {
  totalProjects: number;
  todayProjects: number;
  totalShots: number;
  statusDistribution: Array<{ status: string; count: number }>;
  reviewDistribution: Array<{ reviewStatus: string; count: number }>;
  modelDistribution: Array<{ model: string | null; count: number }>;
  topUsers: Array<{ userId: string; email: string; name: string; projectCount: number }>;
  dailyTrend: Array<{ date: string; count: number }>;
}

const statusMap: Record<string, { label: string; color: string }> = {
  draft: { label: '未规划', color: 'bg-gray-400' },
  planned: { label: '已规划', color: 'bg-blue-400' },
  generating: { label: '生成中', color: 'bg-blue-500' },
  partial: { label: '部分完成', color: 'bg-amber-500' },
  completed: { label: '已完成', color: 'bg-green-500' },
};

const reviewMap: Record<string, { label: string; color: string }> = {
  pending: { label: '待审核', color: 'bg-gray-400' },
  approved: { label: '已通过', color: 'bg-green-500' },
  needs_redo: { label: '待重做', color: 'bg-red-400' },
};

export default function EcommerceSuiteStatsTab() {
  const [stats, setStats] = useState<SuiteStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    platformFetch('/admin/ecommerce-suite/stats')
      .then((res) => res.json())
      .then(setStats)
      .catch((err) => console.error('获取套图统计失败:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>;
  }
  if (!stats) return <p className="text-center py-12 text-light-muted">加载失败</p>;

  const maxDaily = Math.max(...stats.dailyTrend.map((d) => d.count), 1);
  const successShots = stats.statusDistribution.find((s) => s.status === 'success')?.count || 0;
  const failedShots = stats.statusDistribution.find((s) => s.status === 'failed')?.count || 0;
  const finished = successShots + failedShots;
  const successRate = finished > 0 ? Math.round((successShots / finished) * 100) : 0;

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[
          { label: '项目总数', value: stats.totalProjects.toLocaleString(), color: 'bg-blue-500', icon: <ShoppingBag className="w-5 h-5 lg:w-6 lg:h-6 text-white" />, valueClass: 'text-light-text' },
          { label: '今日新建', value: String(stats.todayProjects), color: 'bg-green-500', icon: <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-white" />, valueClass: 'text-green-600' },
          { label: '分镜总数', value: stats.totalShots.toLocaleString(), color: 'bg-purple-500', icon: <Layers className="w-5 h-5 lg:w-6 lg:h-6 text-white" />, valueClass: 'text-accent-blue' },
          { label: '生成成功率', value: `${successRate}%`, color: 'bg-orange-500', icon: <span className="text-white text-lg font-bold">%</span>, valueClass: 'text-orange-500' },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-light-muted text-sm">{card.label}</p>
                <p className={`text-2xl lg:text-3xl font-bold mt-2 ${card.valueClass}`}>{card.value}</p>
              </div>
              <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${card.color} flex items-center justify-center`}>{card.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">分镜生成状态分布</h3>
          <div className="space-y-3">
            {stats.statusDistribution.map((item) => {
              const pct = stats.totalShots > 0 ? Math.round((item.count / stats.totalShots) * 100) : 0;
              const info = statusMap[item.status] || { label: item.status, color: 'bg-gray-300' };
              return (
                <div key={item.status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-light-text">{info.label}</span>
                    <span className="text-light-muted">{item.count}（{pct}%）</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div className={`h-2 rounded-full ${info.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {stats.statusDistribution.length === 0 && <p className="text-sm text-light-muted">暂无数据</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">审核状态分布</h3>
          <div className="space-y-3">
            {stats.reviewDistribution.map((item) => {
              const info = reviewMap[item.reviewStatus] || { label: item.reviewStatus, color: 'bg-gray-300' };
              const pct = stats.totalShots > 0 ? Math.round((item.count / stats.totalShots) * 100) : 0;
              return (
                <div key={item.reviewStatus}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-light-text">{info.label}</span>
                    <span className="text-light-muted">{item.count}（{pct}%）</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div className={`h-2 rounded-full ${info.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {stats.reviewDistribution.length === 0 && <p className="text-sm text-light-muted">暂无数据</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">近 7 天新建项目趋势</h3>
          <div className="flex items-end gap-2 h-40">
            {stats.dailyTrend.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-light-muted">{day.count}</span>
                <div
                  className="w-full rounded-t-lg bg-accent-blue transition-all"
                  style={{ height: `${Math.max((day.count / maxDaily) * 100, 4)}%` }}
                />
                <span className="text-[10px] text-light-muted transform -rotate-45 origin-top-left whitespace-nowrap">{day.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
          <h3 className="text-sm font-medium text-light-muted mb-4">活跃用户 TOP10</h3>
          <div className="space-y-2">
            {stats.topUsers.map((user, index) => (
              <div key={user.userId} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-light-text truncate">
                  <span className="w-5 h-5 rounded-full bg-accent-blue text-white text-xs flex items-center justify-center shrink-0">{index + 1}</span>
                  <span className="truncate">{user.email}</span>
                </span>
                <span className="text-light-muted shrink-0">{user.projectCount} 项目</span>
              </div>
            ))}
            {stats.topUsers.length === 0 && <p className="text-sm text-light-muted">暂无数据</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
